import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { FeedbackCategory, FeedbackQuestion } from '../interfaces/feedback.model';

export interface ChatMessage {
[x: string]: any;
  id: string;
  content: string;
  type: 'bot' | 'user';
  options?: ChatOption[];
  isMultiSelect?: boolean;
  showYesNo?: boolean;
  showLastYesNo?: boolean;
  isEndFlow?: boolean;
  showDetailsInput?: boolean;
  dynamicQuestion?: FeedbackQuestion;
  dynamicQuestionIndex?: number;
  dynamicQuestionTotal?: number;
  timestamp: Date;
}

export interface ChatOption {
  id: string;
  text: string;
  imageUrl?: string;
  nextMessageId?: string;
  specialFlow?: boolean;
  /** Present only for dynamically rendered category options (API-driven flow). */
  dynamicCategory?: FeedbackCategory;
}

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  private messages = new BehaviorSubject<ChatMessage[]>([]);
  private readonly DEFAULT_ISSUE_CONTENT = 'Please select one or more issue(s) you\'re facing';
  private activeFlow = new BehaviorSubject<boolean>(false);
  private readonly chatFlow: { [key: string]: { content: string; options?: ChatOption[]; isMultiSelect?: boolean; showYesNo?: boolean } } = {
    welcome: {
      content: `Dear Patient 👋, We're sorry for the inconvenience. Please select a category to raise the complaint.`,
      options: [
        { id: 'housekeeping',text: 'Housekeeping',imageUrl: 'assets/housekeeping.png',nextMessageId: 'hk_options'},
        { id: 'Food & Beverage', text: 'Food & Beverage', imageUrl: 'assets/food_beverage.png', nextMessageId: 'fb_options' },
        { id: 'Nursing', text: 'Nursing', imageUrl: 'assets/nursing.png', nextMessageId: 'nursing_options' },
        { id: 'Doctor/Medical Team', text: 'Doctor/ Medical Team', imageUrl: 'assets/doctor_team.png', nextMessageId: 'dm_options' },
        { id: 'Pharmacy', text: 'Pharmacy', imageUrl: 'assets/pharmacy.png', nextMessageId: 'ph_options' },
        { id: 'Facilities & Maintenance', text: 'Facilities & Maintenance', imageUrl: 'assets/facilities.png', nextMessageId: 'fm_options' },
        { id: 'Billing & Administration', text: 'Billing & Administration', imageUrl: 'assets/billing.png', nextMessageId: 'ba_options' },
        { id: 'Miscellaneous', text: 'Miscellaneous', imageUrl: 'assets/miscellaneous.png',specialFlow: true},
      ]    },
      hk_options: {
        content: this.DEFAULT_ISSUE_CONTENT,
        options: [
          { id: 'trash', text: 'Unclean wards or restrooms.' },
          { id: 'pest', text: 'Overflowing trash bins.' },
          { id: 'cleaning', text: 'Lack of sanitization supplies(e.g.,soap, tissue).' },
          { id: 'linen', text: 'Pest issues (e.g., mosquitoes, rodents).' },
          { id: 'delay', text: 'Delay in cleaning after discharge.' },
          { id: 'others', text: 'Others (Specify)' }
        ],
        isMultiSelect: true
      }, 
      fb_options: {
        content: this.DEFAULT_ISSUE_CONTENT,
        options: [
          { id: 'quality', text: 'Poor food quality or taste.' },
          { id: 'incorrect_meal', text: 'Incorrect meal delivered (e.g., not matching diet restrictions).' },
          { id: 'delay', text: 'Delay in food delivery.' },
          { id: 'portion', text: 'Inadequate portion sizes.' },
          { id: 'cleanliness', text: 'Utensils or trays not clean.' },
          { id: 'others', text: 'Others (Specify)' }
        ],
        isMultiSelect: true
      },
      nursing_options: {
        content: this.DEFAULT_ISSUE_CONTENT,
        options: [
          { id: 'delay', text: 'Delay in attending to patients.' },
          { id: 'behavior', text: 'Rude or unhelpful behavior.' },
          { id: 'medication', text: 'Incorrect medication administered.' },
          { id: 'explanation', text: 'Failure to explain treatment to the patient/family.' },
          { id: 'mobility', text: 'Lack of timely assistance for mobility (e.g., wheelchairs).' },
          { id: 'others', text: 'Others (Specify)' }
        ],
        isMultiSelect: true
      },
      dm_options: {
        content: this.DEFAULT_ISSUE_CONTENT,
        options: [
          { id: 'visit_delay', text: 'Delay in doctor visits.' },
          { id: 'miscommunication', text: 'Miscommunication regarding diagnosis/treatment.' },
          { id: 'consult_time', text: 'Insufficient time spent on consultations.' },
          { id: 'negligence', text: 'Perceived negligence or oversight.' },
          { id: 'empathy', text: 'Lack of empathy in interactions.' },
          { id: 'others', text: 'Others (Specify)' }
        ],  
        isMultiSelect: true
      },
      ph_options: {
        content: this.DEFAULT_ISSUE_CONTENT,
        options: [
          { id: 'delivery_delay', text: 'Delay in delivering prescribed medicines.' },
          { id: 'unavailability', text: 'Non-availability of essential medication.' },
          { id: 'incorrect_med', text: 'Incorrect medication provided.' },
          { id: 'billing', text: 'Billing discrepancies.' },
          { id: 'expired_med', text: 'Expired medications stocked.' },
          { id: 'others', text: 'Others (Specify)' }
        ],
        isMultiSelect: true
      },
      fm_options: {
        content:this.DEFAULT_ISSUE_CONTENT,
        options: [
          { id: 'ac_issue', text: 'AC or fan not functioning in rooms.' },
          { id: 'lift_issue', text: 'Lift or elevator issues.' },
          { id: 'equipment', text: 'Non-functional hospital equipment (e.g., monitors, beds).' },
          { id: 'seating', text: 'Insufficient seating in waiting areas.' },
          { id: 'infrastructure', text: 'Broken or unsafe infrastructure (e.g., stairs, windows).' },
          { id: 'others', text: 'Others (Specify)' }
        ],
        isMultiSelect: true
      },
      ba_options: {
        content: this.DEFAULT_ISSUE_CONTENT,
        options: [
          { id: 'overcharge', text: 'Overcharging or billing errors.' },
          { id: 'clarity', text: 'Lack of clarity in bills or additional charges.' },
          { id: 'discharge_delay', text: 'Delay in processing discharge paperwork.' },
          { id: 'staff_unavailability', text: 'Unavailability of staff at billing counters.' },
          { id: 'insurance_miscommunication', text: 'Miscommunication regarding insurance policies.' },
          { id: 'others', text: 'Others (Specify)' }
        ],
        isMultiSelect: true
      },
  };

  getMessages(): Observable<ChatMessage[]> {
    return this.messages.asObservable();
  }

  getActiveFlowStatus(): Observable<boolean> {
    return this.activeFlow.asObservable();
  }

  startChat(): void {
    this.messages.next([]);
    this.activeFlow.next(false);
    this.addBotMessage(this.chatFlow['welcome']);
  }

  /** Returns the "Selected Category: ..." message, so callers can remember where this department's thread starts (and later drop it if the user switches departments mid-flow). */
  handleUserSelection(option: ChatOption): ChatMessage {

    this.activeFlow.next(true); 
    const categoryMessage = `Selected Category: ${option.text}`;
    const message = this.addUserMessage(categoryMessage);
    
    
    if (option.specialFlow) {
      // Special flow for Miscellaneous category
      this.addBotMessage({
        content: 'Please provide more details regarding the issue you\'re facing',
        showDetailsInput: true
      });
    } else if (option.nextMessageId && this.chatFlow[option.nextMessageId]) {
      this.addBotMessage(this.chatFlow[option.nextMessageId]);
    }

    return message;
  }

  handleDetailSubmission(details: string, thanks: string): void {
    this.addUserMessage(`Received message: ${details}`);
    this.addBotMessage({
      content: thanks,
      showLastYesNo: true,
      isEndFlow: true
    });
  }

  addBotMessage(messageData: any): void {
    const message: ChatMessage = {
      id: this.generateId(),
      content: messageData.content,
      type: 'bot',
      options: messageData.options,
      isMultiSelect: messageData.isMultiSelect,
      showYesNo: messageData.showYesNo,
      showLastYesNo: messageData.showLastYesNo,
      isEndFlow: messageData.isEndFlow,
      showDetailsInput: messageData.showDetailsInput,
      dynamicQuestion: messageData.dynamicQuestion,
      dynamicQuestionIndex: messageData.dynamicQuestionIndex,
      dynamicQuestionTotal: messageData.dynamicQuestionTotal,
      timestamp: new Date()
    };
    const currentMessages = this.messages.getValue();
    this.messages.next([...currentMessages, message]);
    if (messageData.isEndFlow) {
      this.activeFlow.next(false);
    }
  }

  addUserMessage(content: string): ChatMessage {
    const message: ChatMessage = {
      id: this.generateId(),
      content: content,
      type: 'user',
      timestamp: new Date()
    };
    const currentMessages = this.messages.getValue();
    this.messages.next([...currentMessages, message]);
    return message;
  }

  /**
   * Drops the message with the given id and everything after it from the
   * transcript. Used when the user switches departments mid-flow, so the
   * abandoned department's "Selected Category: ..." line and its unanswered
   * question don't linger in the chat.
   */
  truncateMessagesFrom(messageId: string): void {
    const currentMessages = this.messages.getValue();
    const index = currentMessages.findIndex(m => m.id === messageId);
    if (index !== -1) {
      this.messages.next(currentMessages.slice(0, index));
    }
  }

  handleEndFlow(closing: string): void {
    this.addBotMessage({
      content: closing,
      isEndFlow: true
    });
    this.activeFlow.next(false);
  }

  // =========================================================================
  // Dynamic (API-driven) feedback flow — additive only, legacy path above
  // is completely untouched and keeps working exactly as before for any
  // firm whose /questions/{firmName} response has no per-category questions.
  // =========================================================================

  /** Shown when a department has no icon, or names one this build has no asset for. */
  private static readonly DEFAULT_CATEGORY_IMAGE = 'assets/miscellaneous.png';

  /**
   * The department's own `icon` slug names the asset, so a firm renaming a
   * department keeps its icon and a new vertical ships icons with its seed.
   */
  private resolveDynamicCategoryImage(cat: FeedbackCategory): string {
    return cat.icon
      ? `assets/${cat.icon}.png`
      : ChatService.DEFAULT_CATEGORY_IMAGE;
  }

  startDynamicChat(welcomeMessage: string, categories: FeedbackCategory[]): void {
    this.messages.next([]);
    this.activeFlow.next(false);
    this.addBotMessage({
      content: welcomeMessage,
      options: categories.map(cat => ({
        id: cat.id,
        text: cat.name,
        imageUrl: this.resolveDynamicCategoryImage(cat),
        dynamicCategory: cat
      } as ChatOption))
    });
  }

  /**
   * User picked a dynamic (API-driven) category. Only logs the selection
   * message — ChatComponent owns advancing to the first question, since it
   * owns the per-question index state. Returns the logged message so the
   * caller can remember where this department's thread starts.
   */
  handleDynamicCategorySelection(category: FeedbackCategory): ChatMessage {
    this.activeFlow.next(true);
    return this.addUserMessage(`Selected Category: ${category.name}`);
  }

  addDynamicQuestionMessage(question: FeedbackQuestion, index: number, total: number): void {
    this.addBotMessage({
      content: question.question,
      dynamicQuestion: question,
      dynamicQuestionIndex: index,
      dynamicQuestionTotal: total
    });
  }

  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substring(2);
  }
}